import { Component, DestroyRef, inject, OnInit, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY, Subject, takeUntil } from 'rxjs';
import { ProductsService } from 'src/app/services/products.service';
import { IProduct } from 'src/app/models/products/product';
import { ProductResult } from 'src/app/models/products/product-result';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { EAssetType } from 'src/app/models/products/asset';
import { AuthService } from 'src/app/services/auth.service';
import { Permission } from 'src/app/constants/permission.constant';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatDialog } from '@angular/material/dialog';
import { SignalrService } from 'src/app/services/signalr.service';
import { SignalrGroup } from 'src/app/services/signalr-group';
import { SignalRIdGroups } from 'src/app/constants/signalr.constant';
import { ProductUpdateComponent } from '../product-update/product-update.component';
import { AlertService } from 'src/app/services/alert.service';
import { ClipboardService } from 'src/app/services/clipboard.service';
import { UrlService } from 'src/app/services/url.service';

interface StepperItem {
  id: string;
  label: string;
}

const ALL_STEPS: readonly StepperItem[] = [
  { id: 'preProduction', label: 'pre-production' },
  { id: 'production', label: 'production' },
  { id: 'postProduction', label: 'post-production' },
  { id: 'outputAssets', label: 'output-assets' },
];

const POST_TEAM_STEPS: readonly StepperItem[] = [
  { id: 'production', label: 'production' },
  { id: 'postProduction', label: 'post-production' },
];

const OUTPUT_ONLY_STEPS: readonly StepperItem[] = [
  { id: 'outputAssets', label: 'output-assets' },
];

@Component({
    selector: 'app-product-details-page',
    templateUrl: './product-details-page.component.html',
    styleUrls: ['./product-details-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class ProductDetailsPageComponent implements OnInit {
  product!: IProduct;
  private readonly destroyRef = inject(DestroyRef);
  private destroy$ = new Subject();
  activeStep = 'preProduction';
  private activeStepLabel = '';
  stepperConfig: StepperItem[] = [];
  colPerScreen = 3;

  readonly breakpoint$ = this.breakpointObserver.observe([
    Breakpoints.XLarge,
    Breakpoints.Large,
    Breakpoints.Medium,
    Breakpoints.Small,
  ]);

  backToCollection = false;
  collectionId = '';
  public pageLink = '';

  Permission = Permission;

  constructor(
    public authService: AuthService,
    private productsService: ProductsService,
    public breakpointObserver: BreakpointObserver,
    private clipboard: Clipboard,
    public clipboardService: ClipboardService,
    private router: Router,
    private route: ActivatedRoute,
    public dialog: MatDialog,
    private signalrService: SignalrService,
    public urlService: UrlService,
    private alertService: AlertService,
  ) {
    this.route.queryParams.subscribe((res) => {
      if (res['collectionId'] != null) {
        this.backToCollection = true;
        this.collectionId = res['collectionId'];
      }
    });
  }

  @HostListener('window:resize')
  onResize() {
    this.breakpointChanged();
  }

  copyToClipboard(text: any, param: string) {
    this.clipboard.copy(text);
    this.alertService.infoToast(`Copied product ${param} to clipboard`);
  }

  ngOnInit(): void {
    this.breakpoint$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.breakpointChanged();
    });
    if (!this.product) {
      this.getProductDetail();
    } else {
      this.initialData();
    }
  }

  initialData() {
    this.productsService.setStatePD('id', this.product.id);
    this.stepperConfig = this.getStepperConfig();
    this.activeStep = this.getDefaultActiveStep();
    this.productsService.setStatePD('activeStep', this.activeStep);

    this.joinDetailsGroups();

    const stageParam = this.route.snapshot.paramMap.get('stage');
    this.activeStepLabel = stageParam ? stageParam : this.getDefaultActiveStepLabel();

    if (stageParam) {
      this.activeStep = this.getStepperId(stageParam);
    }

    this.pageLink = this.updatePageLink();
  }

  onBack() {
    const batchId = this.route.snapshot.paramMap.get('batchId');
    this.backToCollection
      ? this.router.navigateByUrl(`collections/${this.collectionId}/products`)
      : this.router.navigateByUrl(`batches/${batchId}/products`);
  }

  openLifecycle() {
    const batchId = this.route.snapshot.paramMap.get('batchId');
    const productId = this.route.snapshot.paramMap.get('productId');
    this.router.navigateByUrl(`/batches/${batchId}/products/${productId}/lifecycle`);
  }

  editCallback = (data: any) => {
    const productId = this.route.snapshot.paramMap.get('productId');
    const batchId = this.route.snapshot.paramMap.get('batchId');
    const { id, assetType, masterSuiteTouchAssetId } = data;
    if (assetType === EAssetType.OUTPUT_ASSETS) {
      this.router.navigate(['batches', batchId, 'products', productId, 'output-assets', id]);
      return;
    }
    const url = `batches/${batchId}/products/${productId}/productTouchAsset/${id}/masterSuiteTouchAsset/${masterSuiteTouchAssetId}/${assetType}`;
    this.router.navigateByUrl(url);
  };

  patchProductNotes(product: IProduct) {
    this.productsService.patchProductNotes(product).subscribe((result) => {
      if (result) {
        product.version = result.version;
      }
    });
  }

  openProductUpdateDialog(product: IProduct) {
    this.dialog.open(ProductUpdateComponent, { data: product });
  }

  openProductLink() {
    if (this.product.productLink) window.open(this.product.productLink, '_blank');
  }

  public onStepSelected(id: string) {
    this.activeStepLabel = this.getStepperLabel(id);
    this.navigationUpdate(this.activeStepLabel);
  }

  private navigationUpdate(activeStepLabel: string) {
    this.activeStep = this.getStepperId(activeStepLabel);
    this.productsService.setStatePD('activeStep', this.activeStep);
    this.productsService.setStatePD('filterBy', '');

    const batchId = this.route.snapshot.paramMap.get('batchId');
    const productId = this.route.snapshot.paramMap.get('productId');
    this.router.navigate(
      [`batches/${batchId}/products/${productId}/${activeStepLabel}`],
      {
        queryParams: this.backToCollection ? { collectionId: this.collectionId } : {},
      },
    );

    this.pageLink = this.updatePageLink();
  }

  private getProductDetail() {
    const productId = this.route.snapshot.paramMap.get('productId');
    if (productId) {
      this.productsService
        .detailsProduct(productId)
        .pipe(takeUntil(this.destroy$))
        .subscribe((result: ProductResult) => {
          this.product = result.product;
          if (result.product.productTouches) {
            this.product.productTouches.sort(
              (a, b) => a.masterSuiteTouchIndex - b.masterSuiteTouchIndex,
            );
          }
          this.initialData();
        });
    }
  }

  private breakpointChanged() {
    switch (true) {
      case this.breakpointObserver.isMatched(Breakpoints.XLarge):
        this.colPerScreen = 5;
        break;
      case this.breakpointObserver.isMatched(Breakpoints.Large):
        this.colPerScreen = 4;
        break;
      case this.breakpointObserver.isMatched(Breakpoints.Medium):
        this.colPerScreen = 3;
        break;
      case this.breakpointObserver.isMatched(Breakpoints.Small):
        this.colPerScreen = 2;
        break;
      default:
        this.colPerScreen = 1;
        break;
    }
  }

  private getStepperConfig(): StepperItem[] {
    if (this.authService.hasPermission(Permission.ProductsWorkflowFilter)) {
      return [...ALL_STEPS];
    }
    if (this.authService.hasPermission(Permission.ShootAssetsRead)) {
      return [...POST_TEAM_STEPS];
    }
    return [...OUTPUT_ONLY_STEPS];
  }

  private getDefaultActiveStep(): string {
    if (this.authService.hasPermission(Permission.ProductsWorkflowFilter)) {
      return 'production';
    }
    if (this.authService.hasPermission(Permission.ShootAssetsRead)) {
      return 'production';
    }
    return 'outputAssets';
  }

  private getDefaultActiveStepLabel(): string {
    if (this.authService.hasPermission(Permission.ProductsWorkflowFilter)) {
      return 'production';
    }
    if (this.authService.hasPermission(Permission.ShootAssetsRead)) {
      return 'production';
    }
    return 'output-assets';
  }

  private getStepperId(queryLabel: string): string {
    const found = this.stepperConfig.find((item) => item.label === queryLabel);
    return found ? found.id : '';
  }

  private getStepperLabel(id: string): string {
    const found = this.stepperConfig.find((item) => item.id === id);
    return found ? found.label : '';
  }

  private updatePageLink(): string {
    const identifier = this.product.barcode ?? this.product.reference ?? '';
    return this.authService.hasPermission(Permission.PostAssetsPreRelease)
      ? `${identifier} - ${this.activeStepLabel}`
      : identifier;
  }

  private joinDetailsGroups() {
    if (!this.authService.hasPermission(Permission.PostAssetsPreRelease)) return;
    SignalrGroup.joinWithId({
      signalrService: this.signalrService,
      destroyRef: this.destroyRef,
      group: SignalRIdGroups.ProductDetails,
      id: this.product.id!,
      data$: EMPTY,
      onData: () => {},
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
